import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";

// Content types for flexible modal
interface ModalLink {
  text: string;
  url: string;
}

interface ModalButton {
  text: string;
  action?: () => void;
  variant?: "primary" | "secondary";
}

interface ModalBullet {
  text: string;
  links?: ModalLink[];
}

interface ModalParagraph {
  text?: string;
  isBold?: boolean;
  bullets?: ModalBullet[];
  links?: ModalLink[];
  buttons?: ModalButton[];
}

interface ModalSection {
  paragraphs: ModalParagraph[];
  images?: string[];
  layout?: "text-only" | "text-image" | "image-text" | "text-top" | "image-top";
  title?: string;
}

interface ModalData {
  category?: string;
  title: string;
  subtitle?: string;
  sections: ModalSection[];
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  data: ModalData;
}

export default function CustomModal({ open, onClose, data }: ModalProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const renderLinks = (links: ModalLink[]) => (
    <div className="flex flex-wrap gap-2 mt-2">
      {links.map((link, idx) => (
        <a
          key={idx}
          href={link.url}
          className="text-blue-600 hover:text-blue-800 underline lg:text-xl text-md"
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.text}
        </a>
      ))}
    </div>
  );

  const renderButtons = (buttons: ModalButton[]) => (
    <div className="flex flex-wrap gap-3 mt-3">
      {buttons.map((button, idx) => (
        <button
          key={idx}
          onClick={button.action}
          className={`px-4 py-2 rounded-lg lg:text-xl text-md font-medium transition-colors ${
            button.variant === "primary"
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
          }`}
        >
          {button.text}
        </button>
      ))}
    </div>
  );

  const renderBullets = (bullets: ModalBullet[]) => (
    <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
      {bullets.map((bullet, idx) => (
        <li key={idx} className="text-gray-600 lg:text-xl text-md">
          {bullet.text}
          {bullet.links && renderLinks(bullet.links)}
        </li>
      ))}
    </ul>
  );

  const renderParagraph = (paragraph: ModalParagraph, idx: number) => (
    <motion.div
      key={idx}
      className="mb-4"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: idx * 0.1 }}
    >
      <p
        className={`text-gray-600 lf:text-xl text-xl leading-relaxed ${
          paragraph.isBold ? "font-semibold" : ""
        }`}
      >
        {paragraph.text}
        {paragraph.links && renderLinks(paragraph.links)}
      </p>
      {paragraph.bullets && renderBullets(paragraph.bullets)}
      {paragraph.buttons && renderButtons(paragraph.buttons)}
    </motion.div>
  );

  const renderSection = (section: ModalSection, sectionIdx: number) => (
    <div key={sectionIdx} className="mb-8">
      {/* {section.title && (
        <motion.h3
          className="text-lg font-semibold text-gray-800 mb-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          {section.title}
        </motion.h3>
      )} */}

      <div
        className={`${
          section.layout === "text-image"
            ? "grid grid-cols-1 lg:grid-cols-1 lg:gap-6 gap-2"
            : section.layout === "image-text"
            ? "grid grid-cols-1 lg:grid-cols-2 lg:gap-6 gap-2"
            : section.layout === "text-top"
            ? "flex flex-col lg:gap-6 gap-2"
            : section.layout === "image-top"
            ? "flex flex-col lg:gap-6 gap-2"
            : "block"
        }`}
      >
        {/* Text Content */}
        <div
          className={`w-full  bg-[#f5f5f7] rounded-3xl ${
            section?.layout === "text-only" || !section?.layout
              ? " p-5"
              : "pb-0 lg:pt-10 pt-5 p-10"
          }`}
        >
          {section?.title && (
            <motion.h3
              className="lg:text-3xl text-lg font-bold text-gray-800 mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {section?.title}
            </motion.h3>
          )}
          <div
            className={`${
              section?.images && section?.images?.length === 0 ? "pb-0" : ""
            } ${
              section.layout === "image-text"
                ? "order-2"
                : section.layout === "image-top"
                ? "order-2"
                : "lg:mb-10 mb-3 "
            }`}
          >
            {section.paragraphs.map((paragraph, idx) =>
              renderParagraph(paragraph, idx)
            )}
          </div>
          {section.images && section.images.length > 0 && (
            <motion.div
              className={`${
                section.layout === "image-text"
                  ? "order-1"
                  : section.layout === "image-top"
                  ? "order-1"
                  : ""
              } lg:space-y-4 space-y-3`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {section.images.map((image, idx) => (
                <motion.div
                  key={idx}
                  className="bg-gray-100 lg:rounded-tl-3xl rounded-tl-xl lg:rounded-br-3xl rounded-br-xl overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                  // whileHover={{ scale: 1.02 }}
                >
                  <img
                    src={image}
                    alt={`Section ${sectionIdx + 1} image ${idx + 1}`}
                    className="w-full lg:h-[517px] h-[257px] object-cover"
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
        {/* <div
          className={
            section.layout === "image-text"
              ? "order-2"
              : section.layout === "image-top"
              ? "order-2"
              : ""
          }
        >
          {section.paragraphs.map((paragraph, idx) =>
            renderParagraph(paragraph, idx)
          )}
        </div> */}

        {/* Images */}
        {/* {section.images && section.images.length > 0 && (
          <motion.div
            className={`${
              section.layout === "image-text"
                ? "order-1"
                : section.layout === "image-top"
                ? "order-1"
                : ""
            } space-y-4`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {section.images.map((image, idx) => (
              <motion.div
                key={idx}
                className="bg-gray-100 rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <img
                  src={image}
                  alt={`Section ${sectionIdx + 1} image ${idx + 1}`}
                  className="w-full h-auto object-cover"
                />
              </motion.div>
            ))}
          </motion.div>
        )} */}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-50 w-full h-full overflow-hidden">
        {/* Overlay */}
        <motion.div
          className="fixed inset-0 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Scrollable modal container */}
        <div
          className="fixed inset-0 flex justify-center items-start md:pt-10 pt-5 overflow-y-auto"
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <motion.div
            className="relative bg-white border-t-2 border-x-2 border-t-[#0071e3] border-x-[#0071e3] rounded-t-2xl shadow-2xl w-full max-w-7xl"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Close Button */}
            <button
              className="sticky  top-5 left-[90%] z-10 w-6 h-6 bg-black bg-opacity-80 text-white rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all cursor-pointer"
              onClick={onClose}
            >
              <IoClose size={32} />
            </button>

            {/* Modal Content */}
            <div className="scroll-smooth">
              {/* Header */}
              <motion.div
                className="lg:pt-20 pt-5 lg:px-20 px-5"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {data.category && (
                  <motion.p
                    className="lg:text-base text-sm text-gray-500 font-medium mb-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                  >
                    {data.category}
                  </motion.p>
                )}
                <motion.h2
                  className="lg:text-4xl text-2xl font-bold text-gray-900 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  {data.title}
                </motion.h2>

                {data.subtitle && (
                  <motion.p
                    className="lg:text-lg text-sm text-gray-600 mt-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                  >
                    {data.subtitle}
                  </motion.p>
                )}
              </motion.div>

              {/* Main Content */}
              <div className="lg:pt-10 pt-5 lg:px-20 px-5 pb-5">
                {data.sections.map((section, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 + idx * 0.1 }}
                  >
                    {renderSection(section, idx)}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
